import React, { Fragment, useEffect, useState } from "react";
import { connect } from "react-redux";

import EntityComponent from "../../../components/common/entity-component";
import Snackbar from "../../../components/common/Snackbar";

import config from "./config";
import EventForm from "./form";
import Store from "../../../types/Store";
import Event from "../../../types/Event";

import Dialog from "../../common/Dialog";
import getAllEvents from "../../../database/events/getAllEvents";
import createEvent from "../../../database/events/createEvent";
import removeEvent from "../../../database/events/removeEvent";
import editEvent from "../../../database/events/editEvent";

import StoreUser from "../../../types/StoreUser";

type Props = {
  store: Store;
  storeUser: StoreUser;
};

// Events content only. The shared page chrome (surface, header, sub-tabs) lives
// in the events/index container so Events and Roundtables sit under one nav item.
const EventsTab = (props: Props) => {
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const { storeUser } = props;

  const eventResponse = getAllEvents({ communityId: storeUser?.communityId });

  useEffect(() => {
    const { error } = eventResponse;
    if (error) {
      setIsSnackbarOpen(true);
    }
  }, [eventResponse]);

  const onCreateEvent = async (event: Event) => {
    setNotificationDialogOpen(true);
    setCurrentEvent({ ...event, communityId: storeUser?.communityId });
  };

  // The database layer no longer swallows write failures, so every action
  // reports what happened. A leak warning matters most: it means a gated
  // event's link reached the public document.
  const reportResult = (result: any) => {
    if (result?.publicDocCarriesProtectedURLs) {
      setActionError(
        "Saved, but the public event still carries a private link. Check the event gating."
      );
    }
  };

  const reportError = (action: string, error: any) => {
    console.error(`Error on event ${action}:`, error);
    setActionError(`Could not ${action} the event. ${error?.message || ""}`);
  };

  const onCreateEventNotif = async (notify: boolean) => {
    setNotificationDialogOpen(false);
    const event = { ...currentEvent, communityId: storeUser?.communityId };
    setCurrentEvent(null);
    try {
      reportResult(await createEvent({ event, notify }));
    } catch (error) {
      reportError("create", error);
    }
  };

  const onRemoveEvent = async ({ id }) => {
    try {
      await removeEvent({ eventId: id });
    } catch (error) {
      reportError("remove", error);
    }
  };

  const onEditEvent = async (eventEdited: Event) => {
    try {
      reportResult(await editEvent({ event: eventEdited }));
    } catch (error) {
      reportError("update", error);
    }
  };

  const renderSnackbar = (response: any): React.ReactNode => {
    const { message, error } = response;
    return (
      <Snackbar
        onCloseSnackbar={() => setIsSnackbarOpen(false)}
        isOpen={isSnackbarOpen}
        message={message}
        error={error ? "Could not load the events." : ""}
      />
    );
  };

  return (
    <Fragment>
      <Dialog
        isOpen={notificationDialogOpen}
        positiveText="Yes"
        title="Notify subscribers"
        negativeText="No"
        negativeAction={() => onCreateEventNotif(false)}
        onCloseDialog={() => {}}
        positiveAction={() => onCreateEventNotif(true)}
        description="Do you want to notify your subscribers about this event?"
      />
      <EntityComponent
        canBeCreated
        canBeRemoved
        canBeEdited
        onRemoveItem={onRemoveEvent}
        onEditItem={onEditEvent}
        onCreateItem={onCreateEvent}
        singularEntityName="Event"
        pluralEntityName="Events"
        filterConfig={config.filterConfig}
        tabConfig={config.tabConfig}
        dataset={eventResponse.events}
        Form={(formProps) => (
          <EventForm communityId={storeUser?.communityId || ""} {...formProps} />
        )}
      />
      {renderSnackbar(eventResponse)}
      {/* Snackbar reads its text from `error` when that prop is set, so the
          message goes there and `message` stays empty. */}
      <Snackbar
        onCloseSnackbar={() => setActionError(null)}
        isOpen={Boolean(actionError)}
        message=""
        error={actionError || ""}
      />
    </Fragment>
  );
};

const mapStateToProps = (state) => ({
  store: state.store,
  storeUser: state.storeUser,
});

export default connect(mapStateToProps, null)(EventsTab);
